/** File d'attente des refunds à valider (maker/checker — spec CAA point 6).
 *
 * - Visible pour tous les rôles authentifiés du tenant (lecture).
 * - Boutons Valider/Rejeter visibles uniquement pour finance + merchant_admin
 *   + admin Moko. Cachés pour l'initiateur du refund (guard visuel — le backend
 *   enforce le vrai check 4-eyes).
 * - Commentaire optionnel à la validation, obligatoire au rejet (≥3 chars).
 */
import { useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  listPendingRefunds,
  rejectRefund,
  validateRefund,
  type Refund,
} from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Modal } from "@/components/Modal";
import { formatAmount, formatDateTime } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, Check, Clock, RotateCcw, X } from "lucide-react";

type ActionState =
  | { kind: "idle" }
  | { kind: "confirming"; action: "validate" | "reject"; refund: Refund }
  | { kind: "submitting"; action: "validate" | "reject"; refund: Refund };

export function PendingRefundsPage() {
  const auth = useAuth();
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<ActionState>({ kind: "idle" });
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listPendingRefunds();
      setRefunds(r.data);
    } catch (e) {
      console.error("failed to load pending refunds", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const currentUserId = auth.status === "authenticated" ? auth.user.id : null;
  const currentRole = auth.status === "authenticated" ? auth.user.role : null;
  // Seuls ces rôles peuvent réellement valider/rejeter. Guard visuel — le backend
  // enforce aussi côté HTTP (403 sinon).
  const canValidate =
    currentRole === "finance" || currentRole === "merchant_admin" || currentRole === "admin";

  const canActOn = (r: Refund): boolean => {
    if (!canValidate) return false;
    // Guard 4-eyes visuel : cache les boutons si le user est l'initiateur.
    if (r.initiated_by_user_id && r.initiated_by_user_id === currentUserId) return false;
    return true;
  };

  const openConfirm = (kind: "validate" | "reject", refund: Refund) => {
    setAction({ kind: "confirming", action: kind, refund });
    setComment("");
    setError(null);
  };

  const closeConfirm = () => {
    if (action.kind === "submitting") return;
    setAction({ kind: "idle" });
    setComment("");
    setError(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (action.kind !== "confirming") return;
    setError(null);
    const trimmed = comment.trim();

    if (action.action === "reject" && trimmed.length < 3) {
      setError("Le motif du rejet doit contenir au moins 3 caractères.");
      return;
    }

    setAction({ kind: "submitting", action: action.action, refund: action.refund });
    try {
      if (action.action === "validate") {
        await validateRefund(action.refund.id, trimmed || undefined);
      } else {
        await rejectRefund(action.refund.id, trimmed);
      }
      setAction({ kind: "idle" });
      setComment("");
      await load();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else if (err instanceof Error) setError(err.message);
      else setError("Erreur inconnue");
      setAction({ kind: "confirming", action: action.action, refund: action.refund });
    }
  };

  return (
    <div>
      <PageHeader
        title="Remboursements à valider"
        breadcrumbs={[{ label: "Finance" }, { label: "À valider" }]}
      />

      <ModelExplainer canValidate={canValidate} />

      {loading ? (
        <div className="card">
          <div className="card-body py-16 text-center text-sm text-[#7e8299]">
            Chargement…
          </div>
        </div>
      ) : refunds.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4">
          {refunds.map((r) => {
            const acting = action.kind !== "idle" && action.refund.id === r.id;
            const canAct = canActOn(r);
            const isOwnRequest = !!(
              r.initiated_by_user_id && r.initiated_by_user_id === currentUserId
            );
            return (
              <div key={r.id} className="card">
                <div className="card-body">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold">
                          <Clock size={12} /> En attente de validation
                        </span>
                        <span className="text-xs font-mono text-[#7e8299]">{r.id}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <Info label="Montant" value={formatAmount(r.amount_minor, r.currency)} highlight />
                        <Info label="Paiement" value={r.payment_id} mono />
                        <Info label="Initié le" value={formatDateTime(r.created_at)} />
                        <Info
                          label="Initiateur"
                          value={r.initiated_by_user_id ?? r.initiated_by ?? "—"}
                          mono
                        />
                      </div>
                      {r.reason && (
                        <div className="rounded-lg bg-[#f5f8fa] p-3 mb-2">
                          <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1">
                            Motif de la demande
                          </div>
                          <div className="text-sm text-[#181c32]">{r.reason}</div>
                        </div>
                      )}
                      {isOwnRequest && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2 mt-2">
                          <AlertCircle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                          <div className="text-xs text-amber-800">
                            Vous êtes l'initiateur — un autre utilisateur doit valider ou rejeter cette demande.
                          </div>
                        </div>
                      )}
                    </div>
                    {canAct && (
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <button
                          onClick={() => openConfirm("validate", r)}
                          disabled={acting}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-success-600 hover:bg-success-700 disabled:bg-success-300 text-white px-4 py-2 text-sm font-semibold"
                        >
                          <Check size={14} /> Valider
                        </button>
                        <button
                          onClick={() => openConfirm("reject", r)}
                          disabled={acting}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-danger-600 hover:bg-danger-700 disabled:bg-danger-300 text-white px-4 py-2 text-sm font-semibold"
                        >
                          <X size={14} /> Rejeter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(action.kind === "confirming" || action.kind === "submitting") && (
        <Modal
          open
          onClose={closeConfirm}
          title={action.action === "validate" ? "Valider le remboursement" : "Rejeter la demande"}
          subtitle={`Refund ${action.refund.id} · ${formatAmount(action.refund.amount_minor, action.refund.currency)}`}
          size="md"
          closeOnOverlay={action.kind === "confirming"}
          footer={
            <>
              <button
                type="button"
                onClick={closeConfirm}
                disabled={action.kind === "submitting"}
                className="btn btn-light btn-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="refund-action-form"
                disabled={action.kind === "submitting"}
                className={`btn btn-sm ${action.action === "validate" ? "btn-success" : "btn-danger"}`}
              >
                {action.action === "validate" ? <Check size={14} /> : <RotateCcw size={14} />}
                {action.kind === "submitting"
                  ? "Traitement…"
                  : action.action === "validate"
                  ? "Confirmer la validation"
                  : "Confirmer le rejet"}
              </button>
            </>
          }
        >
          <form id="refund-action-form" onSubmit={onSubmit} className="space-y-4">
            {action.action === "validate" ? (
              <div className="text-sm text-[#3f4254] leading-relaxed">
                Le payout de <span className="font-bold">{formatAmount(action.refund.amount_minor, action.refund.currency)}</span> sera envoyé au switch immédiatement après validation.
              </div>
            ) : (
              <div className="text-sm text-[#3f4254] leading-relaxed">
                Le remboursement sera annulé et le montant réservé sera relâché sur le paiement. Aucun payout ne sera envoyé.
              </div>
            )}
            <div>
              <label className="form-label">
                Commentaire {action.action === "reject" ? "(obligatoire)" : "(optionnel)"}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required={action.action === "reject"}
                minLength={action.action === "reject" ? 3 : undefined}
                maxLength={500}
                rows={3}
                className="form-input"
                placeholder={
                  action.action === "reject"
                    ? "Ex: montant erroné, doublon de la demande #re_xxx, client déjà remboursé par ailleurs…"
                    : "Ex: validé après contrôle facture VRS"
                }
              />
              <div className="form-hint">
                {action.action === "reject"
                  ? "Le motif du rejet sera visible dans l'historique du refund."
                  : "Le commentaire est enregistré pour audit."}
              </div>
            </div>
            {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          </form>
        </Modal>
      )}
    </div>
  );
}

function Info({ label, value, highlight, mono }: { label: string; value: string; highlight?: boolean; mono?: boolean }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-brand-50" : "bg-[#f5f8fa]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1">{label}</div>
      <div className={`text-sm font-bold ${highlight ? "text-brand-700" : "text-[#181c32]"} ${mono ? "font-mono" : ""} truncate`} title={value}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card">
      <div className="card-body py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-success-50 text-success-700 mb-4">
          <Check size={28} />
        </div>
        <div className="text-lg font-bold text-[#181c32] mb-2">Aucune demande en attente</div>
        <div className="text-sm text-[#7e8299] max-w-md mx-auto leading-relaxed">
          Les remboursements initiés par un utilisateur commercial apparaîtront ici pour validation par un utilisateur finance ou administrateur marchand.
        </div>
      </div>
    </div>
  );
}

function ModelExplainer({ canValidate }: { canValidate: boolean }) {
  return (
    <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 mb-4 flex gap-3">
      <AlertCircle size={18} className="text-brand-700 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-[#3f4254] leading-relaxed">
        <div className="font-semibold text-brand-800 mb-1">Double validation active</div>
        Chaque demande de remboursement doit être validée par un second utilisateur (finance ou administrateur marchand) avant que le payout ne soit envoyé au switch. Un utilisateur ne peut pas valider sa propre demande.
        {!canValidate && (
          <div className="mt-2 text-amber-800">
            Votre rôle ne permet pas de valider les demandes. Vous voyez cette page en lecture seule.
          </div>
        )}
      </div>
    </div>
  );
}
